import { ChangeDetectionStrategy, Component, EventEmitter, Inject,Input, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { BehaviorSubject, filter, finalize, first, iif, map, switchMap, takeUntil } from 'rxjs';

import { Account } from '@personal-area/global/accounts/models';
import { AccountService, AccountsFacade } from '@personal-area/global/accounts/services';
import { UnsubscribeController } from '@personal-area/global/common';
import {
  bonusBannerClosed,
  DepositBonusFacade,
  DepositBonusTermsDialogComponent,
} from '@personal-area/global/deposit-bonus';
import { EventType, GA4Event, GTMService } from '@personal-area/global/gtm';
import { I18nService } from '@personal-area/global/i18n';
import { SubMenuItem } from '@personal-area/global/ui';
import { Role } from '@personal-area/global/users/models';
import { UserFacade } from '@personal-area/global/users/services';
import {
  IDVerificationStatus,
  SummaryVerificationFacade,
} from '@personal-area/global/verification';
import { CryptoStorageService, SESSION_STORAGE } from '@personal-area/storage';

import { getAccountType } from '../../pipes/account-type.pipe';

const OVERVIEW_MENU_ITEM = <SubMenuItem>{
  translateKey: 'dashboard.tab.overview',
  id: 'overview',
  active: true,
};

const PERFORMANCE_MENU_ITEM = <SubMenuItem>{
  translateKey: 'dashboard.tab.performance',
  id: 'performance',
  active: false,
};

@Component({
    selector: 'global-account-viewer',
    templateUrl: './account-viewer.component.html',
    styleUrls: ['./account-viewer.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AccountViewerComponent
  extends UnsubscribeController
  implements OnInit
{
  @Input() accounts: Account[] = [];
  @Input() depositCount = 0;
  @Output() restoreAccountError = new EventEmitter<boolean>();

  public isRestoring$ = new BehaviorSubject<boolean>(false);
  public accountControl = new FormControl<Account | null>(null);
  public isTransactionStatusVisible = false;
  public selectedTab = 'overview';
  public tabs: SubMenuItem[] = [];
  public isBanned$ = this._verificationFacade.summary$.pipe(
    map((summary) => summary.idStatus === IDVerificationStatus.banned),
  );
  protected _roles = Role;
  public depositBonusAvailability$ = this._depositBonusFacade.depositBonusAvailability$;
  public depositBonusList$ = this._depositBonusFacade.depositBonusList$;
  public isFullVerification$ = this._verificationFacade.summary$.pipe(
    map(summary => summary.idStatus === IDVerificationStatus.accepted)
  );
  public isBonusBannerClosed$ = new BehaviorSubject<boolean>(false);

  constructor(
    private _gtmService: GTMService,
    private _accountService: AccountService,
    private _userFacade: UserFacade,
    private _verificationFacade: SummaryVerificationFacade,
    private _accountsFacade: AccountsFacade,
    private _dialog: MatDialog,
    private _i18nService: I18nService,
    private _depositBonusFacade: DepositBonusFacade,
    private _router: Router,
    private _cryptoStorageService: CryptoStorageService,
    @Inject(SESSION_STORAGE) private _sessionStorage: Storage,
  ) {
    super();
  }

  public ngOnInit(): void {
    if (this.accounts.length === 0) {
      return;
    }

    this.accountControl.valueChanges
      .pipe(
        filter(Boolean),
        takeUntil(this._unsubscribe),
      )
      .subscribe((account) => {
        // update account normally, because the change was made by user manually
        this._changeAccountView(account);
        this._accountsFacade.select(account.id);
        this._accountsFacade.selectPrimary(account.id);

        if (account?.id && !account?.isDemo) {
          this._depositBonusFacade.getDepositBonusList(account.id);
          this._depositBonusFacade.getDepositBonusAvailability(account.id, this._i18nService.languageA2);
        }
      });

    this._accountsFacade.getSelectedAccount$
      .pipe(
        filter((account) => !!account),
        filter((account) => {
          const currentAccount = this.accountControl.value;

          if (!currentAccount) {
            return true;
          }

          const isDifferentLogin = currentAccount.login !== account.login;
          const hasBalanceChanged =
            currentAccount.balance !== account.balance ||
            currentAccount.balanceFixed !== account.balanceFixed ||
            currentAccount.equity !== account.equity ||
            currentAccount.credit !== account.credit;

          return isDifferentLogin || hasBalanceChanged;
        }),
        map((account) =>
          [Role.Partner, Role.Exchanger].includes(account.type)
            ? this.accounts[0]
            : account,
        ),
        takeUntil(this._unsubscribe),
      )
      .subscribe((account) => {
        // update account silently, because the outside system already knows the primary
        this._changeAccountView(account);
        this.accountControl.setValue(account, { emitEvent: false });
      });

    if (this.accountControl.value?.id && !this.accountControl.value?.isDemo) {
      this._depositBonusFacade.getDepositBonusAvailability(this.accountControl.value?.id, this._i18nService.languageA2);
      this._depositBonusFacade.getDepositBonusList(this.accountControl.value?.id);
    }

    this.isBonusBannerClosed$
      .next(!!this._cryptoStorageService.getItem(bonusBannerClosed, this._sessionStorage));
  }

  public trackDepositButtonClick(): void {
    this._gtmService.event(EventType.DepositButtonTapped, <GA4Event>{
      context: 'dashboard',
      action: 'tapped',
      object: 'depositButton',
      page_name: 'dashboard',
    });
  }

  public restoreAccount(account: Account): void {
    this.isRestoring$.next(true);

    iif(
      () => account.type === Role.TradingPlatform,
      this._accountService.restoreTPAccount(account.id),
      this._userFacade.user$.pipe(
        first(),
        switchMap(({ id }) =>
          this._accountService.restoreAccount(id, account.id),
        ),
      ),
    )
      .pipe(finalize(() => this.isRestoring$.next(false)))
      .subscribe({
        next: () => this.restoreAccountError.emit(false),
        error: () => this.restoreAccountError.emit(true),
      });
  }

  private _changeAccountView(account: Account): void {
    this.tabs =
      getAccountType(account) === 'archived'
        ? [{ ...OVERVIEW_MENU_ITEM }]
        : [{ ...OVERVIEW_MENU_ITEM }, { ...PERFORMANCE_MENU_ITEM }];
    this.selectedTab =
      this.tabs.find((tab) => tab.active)?.id ?? OVERVIEW_MENU_ITEM.id;
    this.isTransactionStatusVisible =
      !account.isBonus &&
      !account.isDemo &&
      [Role.Trader, Role.TradingPlatform].includes(account.type);
  }

  public onBonusBannerClick(isFullyVerified: boolean | null, wasReceived: boolean, link: string): void {
    if (isFullyVerified) {
      this._dialog.open(DepositBonusTermsDialogComponent, {
        direction: this._i18nService.isRtl ? 'rtl' : 'ltr',
        data: {
          wasFirstBonusReceived: wasReceived,
          termsLink: link,
          fromDeposit: false,
        },
      });

      return;
    }

    void this._router.navigate(['/profile/verification/identity']);
  }

  public onBonusBannerClosed(): void {
    this._cryptoStorageService.setItem(bonusBannerClosed, 'true', this._sessionStorage);
    this.isBonusBannerClosed$.next(true);
  }
}
